import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { TacticalTrack } from "@/services/tactical.service";

const SOURCE = "tactical-tracks";
const LINE = "tactical-tracks-line";
const HEAD = "tactical-tracks-head";

type Props = {
  map: MapLibreMap | null;
  tracks: TacticalTrack[];
};

function toFeatureCollection(tracks: TacticalTrack[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const t of tracks) {
    if (!t.is_visible) continue;
    if (t.geometry?.type === "LineString" && Array.isArray(t.geometry.coordinates)) {
      features.push({
        type: "Feature",
        properties: { id: t.id, color: t.color, name: t.name, role: "line" },
        geometry: t.geometry as GeoJSON.LineString,
      });
    }
    const head = t.head ?? (t.geometry?.type === "Point" ? t.geometry : null);
    if (head?.type === "Point" && Array.isArray(head.coordinates)) {
      features.push({
        type: "Feature",
        properties: { id: t.id, color: t.color, name: t.name, role: "head" },
        geometry: head as GeoJSON.Point,
      });
    }
  }
  return { type: "FeatureCollection", features };
}

/** Overlay треков (линия + head). Не участвует в выборе CAD-фич. */
export function TacticalTracksOverlay({ map, tracks }: Props) {
  useEffect(() => {
    if (!map) return;

    const ensure = () => {
      if (!map.getSource(SOURCE)) {
        map.addSource(SOURCE, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }
      if (!map.getLayer(LINE)) {
        map.addLayer({
          id: LINE,
          type: "line",
          source: SOURCE,
          filter: ["==", ["get", "role"], "line"],
          paint: {
            "line-color": ["coalesce", ["get", "color"], "#2563eb"],
            "line-width": 3,
            "line-opacity": 0.9,
          },
        });
      }
      if (!map.getLayer(HEAD)) {
        map.addLayer({
          id: HEAD,
          type: "circle",
          source: SOURCE,
          filter: ["==", ["get", "role"], "head"],
          paint: {
            "circle-radius": 6,
            "circle-color": ["coalesce", ["get", "color"], "#2563eb"],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#fff",
          },
        });
      }
    };

    const sync = () => {
      try {
        ensure();
        const src = map.getSource(SOURCE) as
          | { setData: (d: GeoJSON.FeatureCollection) => void }
          | undefined;
        src?.setData(toFeatureCollection(tracks));
      } catch {
        /* style not ready */
      }
    };

    if (map.isStyleLoaded()) sync();
    else map.once("load", sync);
    map.on("styledata", sync);
    sync();

    return () => {
      map.off("styledata", sync);
      try {
        if (map.getLayer(HEAD)) map.removeLayer(HEAD);
        if (map.getLayer(LINE)) map.removeLayer(LINE);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
      } catch {
        /* map disposed */
      }
    };
  }, [map, tracks]);

  return null;
}
